import React, { useEffect, useState } from "react";

// region External libraries
import { Control, Controller, useWatch } from "react-hook-form";
import { useLazyQuery, useQuery } from "@apollo/client";
// endregion External libraries
// region Hooks
import { useReachLabel } from "@hooks/useReachLabel";
// endregion Hooks
// region Languages
import useTranslation from "@languages/useTranslation";
import { AnnouncementMessages } from "@languages/interfaces";
// endregion Languages
// region Libraries
import {
  Autocomplete, Radio, RadioGroup, TextField
} from "@libraries/mui/components";
// endregion Libraries
// region Store
import { ECommandResultStatus } from "src/store/graphql/helpers/types.graphql";
import { tenantAllWithPagination, TTenantAllWithPaginationQueries } from "src/store/graphql/groups/tenant/query.graphql";
import {
  announcementRecipientSearchQuery,
  AnnouncementTargetTenant,
  AnnouncementTargetUser,
  TAnnouncementRecipientSearchQuery
} from "src/store/graphql/groups/announcement/query.graphql";
// endregion Store
// region Styles
import * as Styled from "./styles";
// endregion Styles

// region Constants
// Tenants are few enough to load once and filter in the browser, the same way
// the tenant-switch screen does. People are not, hence the search below.
const TENANT_PAGE_SIZE = 200;
const RECIPIENT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 350;
// endregion Constants

// region Interfaces
interface AnnouncementAudienceFieldsProps {
  /** Form control of the parent FormAnnouncement. */
  control: Control<any>;
  /** True while the audience is TARGETED, so the pickers show. */
  targeted: boolean;
}
// endregion Interfaces

/**
 * The "who receives it" block of the announcement form: all customers, or a
 * chosen audience made of whole customers plus individual people. The two
 * lists add up, so a customer and one person elsewhere fit in one announcement.
 */
const AnnouncementAudienceFields: React.FC<AnnouncementAudienceFieldsProps> = ({ control, targeted }) => {

  // region Hooks
  const { t } = useTranslation();
  const reachLabel = useReachLabel();
  // endregion Hooks

  // region States
  const [recipientSearch, setRecipientSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // endregion States

  // region Watched
  const selectedTenants: AnnouncementTargetTenant[] = useWatch({ control, name: "targetTenants", defaultValue: [] });
  const selectedUsers: AnnouncementTargetUser[] = useWatch({ control, name: "targetUsers", defaultValue: [] });
  // endregion Watched

  // region Queries
  const { data: tenantData } = useQuery<TTenantAllWithPaginationQueries>(
    tenantAllWithPagination(["id", "name"]),
    { variables: { page: 1, perPage: TENANT_PAGE_SIZE }, skip: !targeted }
  );

  const [searchRecipients, recipientQuery] = useLazyQuery<TAnnouncementRecipientSearchQuery>(
    announcementRecipientSearchQuery,
    { fetchPolicy: "network-only" }
  );
  // endregion Queries

  // region Constants
  const tenantResult = tenantData?.tenant?.getAllWithPagination;

  const tenantOptions: AnnouncementTargetTenant[] = tenantResult?.status === ECommandResultStatus.SUCCESS
    ? (tenantResult.result ?? []).map((tenant: any) => ({ tenantId: tenant.id, tenantName: tenant.name }))
    : [];

  const recipientOptions = recipientQuery.data?.announcement?.searchAnnouncementRecipients?.records ?? [];

  // Same wording the management listing uses for the same announcement.
  const reach = reachLabel(selectedTenants.length, selectedUsers.length);
  // endregion Constants

  // Types as you go, asks the server once the typing settles.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(recipientSearch), SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [recipientSearch]);

  useEffect(() => {
    if (targeted) searchRecipients({ variables: { search: debouncedSearch, page: 1, perPage: RECIPIENT_PAGE_SIZE } });
  }, [targeted, debouncedSearch, searchRecipients]);

  return (
    <Styled.Container>
      <Styled.Legend>{t(AnnouncementMessages.fieldAudience)}</Styled.Legend>

      <Controller
        name="audience"
        control={control}
        defaultValue="EVERYONE"
        render={({ field }) => (
          <RadioGroup {...field}>
            <Styled.Options>
              <Styled.Option $selected={field.value === "EVERYONE"}>
                <Radio size="small" value="EVERYONE" checked={field.value === "EVERYONE"} />
                <Styled.OptionText>
                  <Styled.OptionTitle $selected={field.value === "EVERYONE"}>
                    {t(AnnouncementMessages.audienceEveryone)}
                  </Styled.OptionTitle>
                  <Styled.OptionHint>{t(AnnouncementMessages.audienceEveryoneHint)}</Styled.OptionHint>
                </Styled.OptionText>
              </Styled.Option>

              <Styled.Option $selected={field.value === "TARGETED"}>
                <Radio size="small" value="TARGETED" checked={field.value === "TARGETED"} />
                <Styled.OptionText>
                  <Styled.OptionTitle $selected={field.value === "TARGETED"}>
                    {t(AnnouncementMessages.audienceTargeted)}
                  </Styled.OptionTitle>
                  <Styled.OptionHint>{t(AnnouncementMessages.audienceTargetedHint)}</Styled.OptionHint>
                </Styled.OptionText>
              </Styled.Option>
            </Styled.Options>
          </RadioGroup>
        )}
      />

      {targeted && (
        <Styled.Pickers>
          <Styled.Field>
            <Controller
              name="targetTenants"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  options={tenantOptions}
                  value={field.value ?? []}
                  onChange={(_event, value) => field.onChange(value)}
                  getOptionLabel={(option) => option.tenantName ?? option.tenantId}
                  isOptionEqualToValue={(option, value) => option.tenantId === value.tenantId}
                  size="small"
                  renderInput={(params) => (
                    <TextField {...params} label={t(AnnouncementMessages.fieldTargetTenants)} size="small" />
                  )}
                />
              )}
            />
            <Styled.Hint>{t(AnnouncementMessages.targetTenantsHint)}</Styled.Hint>
          </Styled.Field>

          <Styled.Field>
            <Controller
              name="targetUsers"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  options={recipientOptions}
                  value={field.value ?? []}
                  onChange={(_event, value) => field.onChange(value)}
                  onInputChange={(_event, value) => setRecipientSearch(value)}
                  filterOptions={(options) => options}
                  loading={recipientQuery.loading}
                  loadingText={t(AnnouncementMessages.recipientSearching)}
                  noOptionsText={t(AnnouncementMessages.recipientNoResults)}
                  getOptionLabel={(option: AnnouncementTargetUser) => (
                    option.tenantName ? `${option.userName} (${option.tenantName})` : `${option.userName}`
                  )}
                  renderOption={(optionProps, option: AnnouncementTargetUser) => (
                    <li {...optionProps} key={option.userId}>
                      <Styled.Recipient>
                        <Styled.RecipientName>{option.userName}</Styled.RecipientName>
                        <Styled.RecipientDetails>
                          {option.userEmail && <span>{option.userEmail}</span>}
                          {option.tenantName && <Styled.RecipientTenant>{option.tenantName}</Styled.RecipientTenant>}
                        </Styled.RecipientDetails>
                      </Styled.Recipient>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) => option.userId === value.userId}
                  size="small"
                  renderInput={(params) => (
                    <TextField {...params} label={t(AnnouncementMessages.fieldTargetUsers)} size="small" />
                  )}
                />
              )}
            />
            <Styled.Hint>{t(AnnouncementMessages.targetUsersHint)}</Styled.Hint>
          </Styled.Field>

          <Styled.Summary $filled={Boolean(reach)}>
            {reach ? (
              <>
                <Styled.SummaryLabel>{t(AnnouncementMessages.audienceSummaryLabel)}</Styled.SummaryLabel>
                <span>{reach}</span>
              </>
            ) : (
              <span>{t(AnnouncementMessages.audienceSummaryEmpty)}</span>
            )}
          </Styled.Summary>
        </Styled.Pickers>
      )}
    </Styled.Container>
  );
};

export default AnnouncementAudienceFields;
